import { useEffect, useRef, useState } from 'react'
import { postCTA } from '../../utils/appBridge'
import { getEliteCTA } from '../../utils/mutualFundDetail'
import RenderHTML from '../ui-components/RenderHTML'

export const EliteBanner = ({ onBannerClick }: { onBannerClick?: () => void }) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const bannerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setShouldAnimate(true)
          }, 500)
        }
      },
      { threshold: 0.1 }
    )

    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <button
      type='button'
      ref={bannerRef}
      onClick={() => {
        onBannerClick?.()
        postCTA(JSON.stringify(getEliteCTA()))
      }}
      aria-description='Double tap to activate'
      className='relative z-0 flex gap-4 font-[400] leading-[140%] bg-[#FFF7EA] w-full text-left appearance-none
    border-x-0 border-t-0 border-b border-solid border-[#FFF] items-center py-5 px-5 text-[#722200] text-[13px] overflow-hidden cursor-pointer'
    >
      <div
        aria-hidden='true'
        className='absolute -z-10 top-0 left-7 w-full h-full scale-125'
        style={{
          animation: shouldAnimate ? 'shine 1.2s ease-out forwards' : 'none',
          transform: shouldAnimate ? 'translateX(-100%) scale(1.25)' : 'translateX(-100%) scale(1.25)',
          opacity: 1
        }}
      >
        <RenderHTML innerHTMLText={ShineIcon} />
      </div>
      <div aria-hidden='true'>
        <RenderHTML innerHTMLText={LeafsBadge} />
      </div>
      <div className='flex-1'>
        {/* #722200 on #FFF7EA ≥4.5:1 (WCAG 1.4.3, Reg_SeeWhy_B01); #CC5C2D was 3.8:1 */}
        Unlock Power Rank and Scores for <br /> confident investing with{' '}
        <span className='font-[500]'> PowerUp Elite</span>
      </div>
      <div aria-hidden='true'>
        <RenderHTML innerHTMLText={RightChevron} />
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes shine {
              0% {
                transform: translateX(-100%) scale(1.25);
                opacity: 1;
              }
              40% {
                transform: translateX(-20%) scale(1.25);
                opacity: 1;
              }
              100% {
                transform: translateX(100%) scale(1.25);
                opacity: 0;
              }
            }
          `
        }}
      />
    </button>
  )
}

export const LeafsBadge = `<svg width="39" height="20" viewBox="0 0 39 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M10.1749 1.86264C10.5047 1.33625 10.3453 0.642185 9.81895 0.312381C9.29256 -0.0174039 8.59849 0.141964 8.26869 0.66835L7.78756 1.43631C7.45776 1.96269 7.61713 2.65676 8.14353 2.98655C8.66992 3.31635 9.36399 3.15698 9.69377 2.6306L10.1749 1.86264ZM4.18364 3.18821C4.63463 2.75577 5.34687 2.76636 5.77448 3.21183L6.84944 4.33169L8.65664 3.83115C9.25731 3.66478 9.87355 4.01481 10.033 4.61296C10.1926 5.2111 9.83493 5.83086 9.23426 5.99723L6.78136 6.6766C6.38334 6.78685 5.95937 6.67175 5.67599 6.37656L4.14135 4.77782C3.71374 4.33234 3.73267 3.62066 4.18364 3.18821ZM4.01812 7.01977C3.54813 6.61924 2.83649 6.67827 2.42864 7.1516C2.02078 7.62492 2.07115 8.33331 2.54114 8.73384L4.70972 10.5819C5.04061 10.8638 5.50795 10.9271 5.90793 10.744L8.52936 9.54429C9.09747 9.28427 9.35222 8.61718 9.09832 8.05428C8.84442 7.4914 8.17803 7.24587 7.60989 7.50588L5.64423 8.40551L4.01812 7.01977ZM8.53096 13.3974C9.06841 13.084 9.25128 12.3954 8.9394 11.8594C8.62752 11.3233 7.939 11.1428 7.40152 11.4562L5.65503 12.4745L3.49496 11.6028C2.91941 11.3706 2.26292 11.6491 2.02864 12.225C1.79436 12.8008 2.071 13.4559 2.64655 13.6882L5.31799 14.7662C5.64054 14.8964 6.0057 14.8698 6.30691 14.6941L8.53096 13.3974Z" fill="#CC5C2D"/>
<path d="M11.3423 14.5119L10.5629 16.0036C11.3704 16.068 13.4779 16.5487 15.4479 17.9571C15.934 18.3436 16.0146 19.0512 15.628 19.5375C15.2413 20.0235 14.5337 20.1041 14.0477 19.7176C13.7223 19.3652 12.4827 18.5448 10.1267 18.0826C9.7379 18.0314 9.37817 18.0689 9.03145 18.0826L8.89151 18.0689C8.87564 18.0701 8.8597 18.071 8.84367 18.0716L5.14246 18.2109C4.51535 18.2344 3.97554 17.7563 3.93678 17.1428C3.898 16.5292 4.37492 16.0128 5.00202 15.9892L8.0517 15.8744L9.31083 13.4646C9.59379 12.923 10.2779 12.7184 10.8389 13.0076C11.3999 13.2968 11.6252 13.9703 11.3423 14.5119Z" fill="#CC5C2D"/>
<path d="M19.4138 5.06875C19.0454 5.39375 18.6446 5.65375 18.2113 5.84875C17.7888 6.04375 17.3121 6.19542 16.7813 6.30375V4.5975C17.3229 4.47833 17.8483 4.2725 18.3575 3.98C18.8667 3.67667 19.3217 3.30833 19.7225 2.875H21.2013V14.25H19.4138V5.06875Z" fill="#722200"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M28.5751 1.86264C28.2453 1.33625 28.4047 0.642185 28.9311 0.312381C29.4574 -0.0174039 30.1515 0.141964 30.4813 0.66835L30.9624 1.43631C31.2922 1.96269 31.1329 2.65676 30.6065 2.98655C30.0801 3.31635 29.386 3.15698 29.0562 2.6306L28.5751 1.86264ZM34.5664 3.18821C34.1154 2.75577 33.4031 2.76636 32.9755 3.21183L31.9006 4.33169L30.0934 3.83115C29.4927 3.66478 28.8765 4.01481 28.717 4.61296C28.5574 5.2111 28.9151 5.83086 29.5157 5.99723L31.9686 6.6766C32.3667 6.78685 32.7906 6.67175 33.074 6.37656L34.6086 4.77782C35.0363 4.33234 35.0173 3.62066 34.5664 3.18821ZM34.7319 7.01977C35.2019 6.61924 35.9135 6.67827 36.3214 7.1516C36.7292 7.62492 36.6788 8.33331 36.2089 8.73384L34.0403 10.5819C33.7094 10.8638 33.2421 10.9271 32.8421 10.744L30.2206 9.54429C29.6525 9.28427 29.3978 8.61718 29.6517 8.05428C29.9056 7.4914 30.572 7.24587 31.1401 7.50588L33.1058 8.40551L34.7319 7.01977ZM30.219 13.3974C29.6816 13.084 29.4987 12.3954 29.8106 11.8594C30.1225 11.3233 30.811 11.1428 31.3485 11.4562L33.095 12.4745L35.255 11.6028C35.8306 11.3706 36.4871 11.6491 36.7214 12.225C36.9556 12.8008 36.679 13.4559 36.1035 13.6882L33.432 14.7662C33.1095 14.8964 32.7443 14.8698 32.4431 14.6941L30.219 13.3974Z" fill="#CC5C2D"/>
<path d="M27.4077 14.5119L28.1871 16.0036C27.3796 16.068 25.2721 16.5487 23.3021 17.9571C22.816 18.3436 22.7354 19.0512 23.122 19.5375C23.5087 20.0235 24.2163 20.1041 24.7023 19.7176C25.0277 19.3652 26.2673 18.5448 28.6233 18.0826C29.0121 18.0314 29.3718 18.0689 29.7186 18.0826L29.8585 18.0689C29.8744 18.0701 29.8903 18.071 29.9063 18.0716L33.6075 18.2109C34.2346 18.2344 34.7745 17.7563 34.8132 17.1428C34.852 16.5292 34.3751 16.0128 33.748 15.9892L30.6983 15.8744L29.4392 13.4646C29.1562 12.923 28.4721 12.7184 27.9111 13.0076C27.3501 13.2968 27.1248 13.9703 27.4077 14.5119Z" fill="#CC5C2D"/>
</svg>
`

export const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <g clip-path="url(#clip0_39936_7729)">
    <path d="M10.4932 5.74398L4.75324 0.283978C4.65268 0.188376 4.53426 0.113518 4.40477 0.0636779C4.27527 0.0138379 4.13723 -0.0100078 3.99851 -0.00649773C3.8598 -0.00298769 3.72314 0.0278094 3.59633 0.0841351C3.46952 0.140461 3.35504 0.221212 3.25944 0.321778C3.06636 0.524881 2.96188 0.796365 2.96897 1.07651C2.97606 1.35665 3.09414 1.6225 3.29724 1.81558L8.49404 6.75058C8.52853 6.78316 8.55601 6.82244 8.57478 6.86601C8.59356 6.90959 8.60325 6.95653 8.60325 7.00398C8.60325 7.05143 8.59356 7.09837 8.57478 7.14194C8.55601 7.18552 8.52853 7.2248 8.49404 7.25738L3.29724 12.1924C3.09525 12.3842 2.97772 12.6483 2.9705 12.9268C2.96328 13.2052 3.06696 13.4751 3.25874 13.6771C3.45052 13.8791 3.71468 13.9966 3.99312 14.0038C4.27155 14.011 4.54145 13.9074 4.74344 13.7156L10.5072 8.24158C10.6712 8.07718 10.8011 7.88192 10.8892 7.66708C10.9773 7.45224 11.022 7.22207 11.0207 6.98986C11.0194 6.75765 10.9721 6.528 10.8816 6.31416C10.7911 6.10032 10.6591 5.90653 10.4932 5.74398Z" fill="#CC5C2D"/>
  </g>
  <defs>
    <clipPath id="clip0_39936_7729">
      <rect width="14" height="14" fill="white" transform="translate(0 14) rotate(-90)"/>
    </clipPath>
  </defs>
</svg>`

const ShineIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="176" height="75" viewBox="0 0 176 75" fill="none">
  <g opacity="0.7" filter="url(#filter0_f_39936_7724)">
    <path d="M103.094 -8.44769H164.918L72.9579 123.512L11.1345 123.512L103.094 -8.44769Z" fill="white"/>
  </g>
  <defs>
    <filter id="filter0_f_39936_7724" x="0.134521" y="-19.4478" width="175.783" height="153.96" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="5.5" result="effect1_foregroundBlur_39936_7724"/>
    </filter>
  </defs>
</svg>`
